import type { ReactNode } from "react";
import { FileText } from "lucide-react";

import { cn } from "@/lib/utils";

type PdfFileCardProps = {
  filename: string;
  /**
   * Optional small action area (e.g. a "move" button in Phase 3).
   * In Phase 2 the file list is read-only, so this is unused there.
   */
  actions?: ReactNode;
  /**
   * Optional leading badge slot (e.g. a confidence dot or a
   * "duplicate filename" mark).
   */
  leadingBadge?: ReactNode;
  /**
   * `"default"` is comfortable spacing for review cards;
   * `"compact"` is tight for high-density lists.
   */
  density?: "default" | "compact";
};

/**
 * Mini card for a single PDF filename.
 *
 * Chosen over a chip because filenames are often long and chips truncate
 * badly. The card lets the filename wrap onto multiple lines (with
 * `break-all` to handle long unbroken sequences), keeping every
 * character readable. The full filename is also exposed via `title` so
 * hover gives a tooltip.
 *
 * Purely presentational. No state, no callbacks. Optional `actions` and
 * `leadingBadge` slots let phase-specific UIs add density-appropriate
 * controls without changing this component.
 */
export function PdfFileCard({
  filename,
  actions,
  leadingBadge,
  density = "default",
}: PdfFileCardProps) {
  const isCompact = density === "compact";
  return (
    <div
      className={cn(
        "flex flex-row items-start gap-2 rounded-md border bg-card",
        isCompact ? "px-2 py-1" : "px-3 py-2",
      )}
      title={filename}
    >
      {leadingBadge ? (
        <div className="shrink-0 flex items-center pt-0.5">{leadingBadge}</div>
      ) : null}
      <FileText
        className={cn(
          "shrink-0 text-muted-foreground",
          isCompact ? "h-3.5 w-3.5 mt-0.5" : "h-4 w-4 mt-0.5",
        )}
        aria-hidden
      />
      <div
        className={cn(
          "flex-1 min-w-0 font-mono break-all",
          isCompact ? "text-[11px] leading-snug" : "text-xs leading-snug",
        )}
      >
        {filename}
      </div>
      {actions ? (
        <div className="shrink-0 flex items-center gap-1">{actions}</div>
      ) : null}
    </div>
  );
}
